<script setup lang="ts">
import { HgSupportedLanguage } from '@smg-real-estate/domus';
import { useHead } from '@unhead/vue';
import { storeToRefs } from 'pinia';
import { computed, onBeforeUnmount, onServerPrefetch, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';

import { fetchAgents } from '@/api/agentMetaApi';
import DirectoryBreadcrumbs from '@/components/Breadcrumbs/DirectoryBreadcrumbs.vue';
import DirectorySeoLinks from '@/components/DirectorySeoLinks/DirectorySeoLinks.vue';
import { useDirectoryRouteFacets } from '@/composables/useDirectoryRouteFacets';
import { useDirectoryRouteLocations } from '@/composables/useDirectoryRouteLocations';
import { localizeLanguage } from '@/helpers/Locale/localizeLanguage';
import { isPlatformHg, isPlatformIs24 } from '@/helpers/Platform/platform';
import { isPageIndexable } from '@/helpers/SEO/isPageIndexable';
import { countryTextMapping } from '@/helpers/SEO/metaInfoHelper';
import AgentResultList from '@/pages/AgentDirectoryPage/AgentResultList.vue';
import HeroBanner from '@/pages/AgentDirectoryPage/HeroBanner.vue';
import { syncAgentDirectoryFromRoute } from '@/pages/AgentDirectoryPage/syncAgentDirectoryFromRoute';
import { getAgentBasePathWithLocation } from '@/router';
import { useLocale } from '@/router/routeMeta';
import { useAgentProfilesStore } from '@/stores/agentProfiles';
import { LoadState } from '@/stores/types';

const { t } = useI18n();
const language = useLocale();
const route = useRoute();

const agentProfilesStore = useAgentProfilesStore();
const {
  agents,
  agentsLoadState,
  totalAgentsCount,
  agentListCurrentPage,
  pageCount,
  searchedLocations,
} = storeToRefs(agentProfilesStore);

const currentPage = computed(() => {
  const page = Number.parseInt((route.query.page as string) ?? 1, 10);

  if (Number.isNaN(page) || page < 1) {
    return 1;
  }

  return page;
});

const filterLocations = useDirectoryRouteLocations();
const filterFacets = useDirectoryRouteFacets();

const syncAgentQueryStashFromRoute = () => {
  agentProfilesStore.syncLastAgentDirectoryQueryFromRoute(route.query);
};

const loadFromRoute = () => {
  syncAgentQueryStashFromRoute();

  return syncAgentDirectoryFromRoute({
    geoLocations: filterLocations.value,
    locale: language.value,
    currentPage: currentPage.value,
    facets: filterFacets.value,
  });
};

onServerPrefetch(loadFromRoute);

watch(
  [currentPage, filterLocations, filterFacets],
  async () => {
    await loadFromRoute();
  },
  { immediate: true },
);

onBeforeUnmount(() => {
  agentProfilesStore.markSearchedLocationsStale();
});

const hasSearchedLocations = computed(() =>
  Boolean(searchedLocations.value && searchedLocations.value.length > 0),
);

const locationNameForSeo = computed(() => {
  const locations = searchedLocations.value;

  return locations && locations.length > 0
    ? locations.map((item) => item.names[language.value]).join(',')
    : countryTextMapping[language.value];
});

const canonicalUrl = computed(() => {
  const pageNumber = currentPage.value > 1 ? `?page=${currentPage.value}` : '';
  return `${import.meta.env.VUE_APP_BASE_URL}${route.path}${pageNumber}`;
});

const title = computed(() => {
  const count = totalAgentsCount.value;

  if (currentPage.value > 1) {
    return t('seo.agentPageTitleWithPageLocationSpecific', {
      page: currentPage.value,
      location: locationNameForSeo.value,
      count,
    });
  }

  if (hasSearchedLocations.value) {
    return t('seo.agentPageTitleLocationSpecific', {
      location: locationNameForSeo.value,
      count,
    });
  }

  return t('seo.agentPageTitleNational');
});

const description = computed(() => {
  if (hasSearchedLocations.value) {
    return t('seo.agentPageDescriptionLocationSpecific', {
      location: locationNameForSeo.value,
      count: totalAgentsCount.value,
    });
  }

  return t('seo.agentPageDescriptionNational');
});

const shouldDeIndex = computed(() => !isPageIndexable(searchedLocations.value, currentPage.value));

const getLocalizedAgentDirectoryUrl = (lang: HgSupportedLanguage) =>
  `${import.meta.env.VUE_APP_BASE_URL}${getAgentBasePathWithLocation(
    lang,
    searchedLocations.value?.[0]?.urlNames?.[lang],
  )}`;

const seoLinks = computed(() => [
  {
    rel: 'canonical' as const,
    href: canonicalUrl.value,
  },
  ...Object.values(HgSupportedLanguage).map((lang) => ({
    rel: 'alternate' as const,
    hreflang: localizeLanguage(lang),
    href: getLocalizedAgentDirectoryUrl(lang),
  })),
  {
    rel: 'alternate' as const,
    hreflang: 'x-default',
    href: getLocalizedAgentDirectoryUrl(HgSupportedLanguage.DE),
  },
]);

useHead({
  title,
  meta: () => [
    {
      name: 'description',
      content: () => description.value,
    },
    ...(shouldDeIndex.value
      ? [
          { name: 'robots', content: 'noindex, nofollow' },
          { 'http-equiv': 'x-robots-tag', content: 'noindex, nofollow' },
        ]
      : []),
  ],
  link: seoLinks,
});
</script>

<template>
  <div class="layout-container">
    <DirectoryBreadcrumbs
      v-if="isPlatformIs24() && searchedLocations !== null"
      directoryType="agent"
      :searchedLocations="searchedLocations ?? []"
      :includeStructuredData="false"
      class="breadcrumbs"
    />
  </div>

  <HeroBanner />

  <div class="layout-container">
    <AgentResultList
      :loadingAgents="agentsLoadState === LoadState.LOADING"
      :agentsLoadFailed="agentsLoadState === LoadState.FAILED"
      :agents="agents"
      :totalAgentsCount="totalAgentsCount"
      :agentListCurrentPage="agentListCurrentPage"
      :pageCount="pageCount"
      :language="language"
      :locationsForSeo="locationNameForSeo"
    />
    <DirectoryBreadcrumbs
      v-if="isPlatformHg() && searchedLocations !== null"
      directoryType="agent"
      :searchedLocations="searchedLocations ?? []"
      :includeStructuredData="false"
      class="breadcrumbs"
    />
    <DirectorySeoLinks
      directoryType="agent"
      :searchedLocations="searchedLocations ?? []"
      :inventoryFetcher="fetchAgents"
    />
  </div>
</template>

<style scoped lang="scss">
@use '@/assets/variables' as *;

.breadcrumbs {
  margin-top: $space-150;
}
</style>
